/**
 * Preview mode: the portal, rendered for an agent, against a real athlete.
 *
 * The portal's own pages are not rebuilt for this. They read through
 * getClientSession() / getClientPb() / portalRoute() like always; those three
 * consult this module, and in preview mode they point at
 * /api/portal/preview/<accountId>/… carrying the AGENT's staff token. The
 * server swaps it for a short-lived impersonation token, so PocketBase
 * applies the athlete's own rules and what comes back is what the athlete
 * would get — not a second implementation of the visibility rules that could
 * drift from the real one.
 *
 * Nothing here holds a portal token. The agent's own token is what travels,
 * and the server decides what it may see.
 *
 * Writes are refused by the server (GET only), so preview is read-only even
 * if a page tries. `readOnlyGuard()` exists so a page can fail politely
 * before making a request that was always going to be refused.
 */

import type { ClientUser } from '../../types/clientPortal'

export interface PreviewState {
  /** The client_users record being previewed. */
  accountId: string
  /** The agent's own PocketBase token — never the athlete's. */
  agentToken: string
  /** The athlete's real account record, switches and all. */
  user: ClientUser
}

/**
 * Kept in sessionStorage, not memory alone: an agent who reloads mid-preview
 * would otherwise be bounced to the portal sign-in page. It dies with the
 * tab, and the only credential in it is the agent's own token, which their
 * staff session already keeps in this browser.
 */
const KEY = 'reppr_portal_preview'

function hydrate(): PreviewState | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.accountId || !parsed?.agentToken || !parsed?.user) return null
    return parsed as PreviewState
  } catch {
    return null
  }
}

let state: PreviewState | null = hydrate()
const listeners = new Set<() => void>()

export function enterPreview(next: PreviewState): void {
  state = next
  try { sessionStorage.setItem(KEY, JSON.stringify(next)) } catch { /* private mode: memory is enough */ }
  listeners.forEach(fn => fn())
}

export function exitPreview(): void {
  state = null
  try { sessionStorage.removeItem(KEY) } catch { /* nothing to clear */ }
  listeners.forEach(fn => fn())
}

export function previewState(): PreviewState | null {
  return state
}

export function isPreview(): boolean {
  return state !== null
}

export function onPreviewChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** The base a PocketBase client should talk to while previewing. */
export function previewPocketBaseBase(): string {
  if (!state) throw new Error('Not previewing')
  return `/api/portal/preview/${encodeURIComponent(state.accountId)}/pb`
}

/**
 * Where a portal server route lives.
 *
 * Normally /api/portal/deals. In preview it goes through the proxy, which
 * calls the same handler with an impersonation token — so the route cannot
 * tell the difference and neither can its output.
 */
export function portalRoutePath(name: 'deals' | 'contracts' | 'money' | 'schedule'): string {
  if (!state) return `/api/portal/${name}`
  return `/api/portal/preview/${encodeURIComponent(state.accountId)}/route/${name}`
}

/**
 * Stops a write before it is attempted. The server refuses these anyway;
 * this turns a 405 into a sentence the agent can read.
 */
export function readOnlyGuard(what: string): void {
  if (state) {
    throw new Error(`This is a preview of ${state.user.name || 'your client'}’s portal — ${what} is not possible from here.`)
  }
}
